/* File drops and the file picker. Dropped files are added by the app's own
   window-level drop handler (App.tsx); drop zones only show that they're live. */

import { useEffect, useRef, useState } from 'react';
import { useStore, addFiles } from '../store/store';

export function useFileDrop() {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  return {
    over,
    bind: {
      onDragEnter: (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); depth.current++; setOver(true); },
      onDragOver: (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; },
      onDragLeave: () => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); },
      onDrop: () => { depth.current = 0; setOver(false); }
    }
  };
}
export function hasFiles(e: { dataTransfer: DataTransfer | null }) { return Array.from(e.dataTransfer?.types || []).includes('Files'); }

export function FilePicker({ children, className, multiple = true, label, tip }: { children: React.ReactNode; className: string; multiple?: boolean; label?: string; tip?: string }) {
  const picker = useFilePicker(multiple);
  return (
    <>
      <button type="button" className={className} aria-label={label} data-tip={tip} onClick={picker.open}>{children}</button>
      {picker.input}
    </>
  );
}

/** The hidden file input and a function that opens it (for big clickable drop areas). */
export function useFilePicker(multiple = true) {
  const ref = useRef<HTMLInputElement>(null);
  const accept = useStore(s => s.config?.builder.upload.accept.map(e => '.' + e).join(',') || 'image/*,.pdf,.svg');
  return {
    open: () => ref.current?.click(),
    input: <input ref={ref} type="file" hidden multiple={multiple} accept={accept} onChange={e => { if (e.target.files) void addFiles(e.target.files); e.target.value = ''; }} />
  };
}

/** true on phones / narrow tablets (the workspace switches to bottom bars and sheets) */
export function useMedia(q: string): boolean {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [q]);
  return m;
}
